import { ApiResponseError, type ApiClient } from "@shell/auth/apiClient";
import { exportListResponseSchema } from "../common/schemas";
import {
    workspaceAssignmentListResponseSchema,
    workspaceAssignmentMutationResponseSchema,
    workspaceAssignmentViewSchema,
    type WorkspaceAssignmentCreatePayload,
    type WorkspaceAssignmentMutationResult,
    type WorkspaceAssignmentView,
} from "./types";

interface SearchParams {
    readonly workspaceId?: string;
    readonly userId?: string;
    readonly open?: boolean;
}

export async function searchWorkspaceAssignments(
    client: ApiClient,
    params: SearchParams,
): Promise<readonly WorkspaceAssignmentView[]> {
    const search = new URLSearchParams();
    if (params.workspaceId !== undefined && params.workspaceId.length > 0)
        search.set("workspaceId", params.workspaceId);
    if (params.userId !== undefined && params.userId.length > 0)
        search.set("userId", params.userId);
    if (params.open !== undefined) search.set("open", params.open ? "true" : "false");

    const path =
        search.size > 0
            ? `/api/workspace-assignments?${search.toString()}`
            : "/api/workspace-assignments";
    const raw = await client.getJson<unknown>(path);
    return workspaceAssignmentListResponseSchema.parse(raw).results;
}

export async function exportWorkspaceAssignments(
    client: ApiClient,
    params: SearchParams,
): Promise<{ readonly results: readonly WorkspaceAssignmentView[]; readonly truncated: boolean }> {
    const search = new URLSearchParams();
    if (params.workspaceId !== undefined && params.workspaceId.length > 0)
        search.set("workspaceId", params.workspaceId);
    if (params.userId !== undefined && params.userId.length > 0)
        search.set("userId", params.userId);
    if (params.open !== undefined) search.set("open", params.open ? "true" : "false");

    const path =
        search.size > 0
            ? `/api/exports/workspace-assignments?${search.toString()}`
            : "/api/exports/workspace-assignments";
    const raw = await client.getJson<unknown>(path);
    return exportListResponseSchema(workspaceAssignmentViewSchema).parse(raw);
}

export async function createWorkspaceAssignment(
    client: ApiClient,
    payload: WorkspaceAssignmentCreatePayload,
): Promise<WorkspaceAssignmentMutationResult> {
    const body: Record<string, unknown> = {
        id: payload.id,
        workspaceId: payload.workspaceId,
        userId: payload.userId,
    };

    const response = await client.fetch("/api/workspace-assignments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    return await readMutationResponse(response);
}

export async function releaseWorkspaceAssignment(
    client: ApiClient,
    workspaceId: string,
    assignmentId: string,
): Promise<WorkspaceAssignmentView> {
    const response = await client.fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/assignments/${encodeURIComponent(assignmentId)}/release`,
        { method: "PATCH" },
    );
    const result = await readMutationResponse(response);
    return result.assignment;
}

async function readMutationResponse(
    response: Response,
): Promise<WorkspaceAssignmentMutationResult> {
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    const parsed = workspaceAssignmentMutationResponseSchema.parse(raw);
    return {
        assignment: parsed.assignment,
        replacedAssignment: parsed.replacedAssignment ?? null,
    };
}
